import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Upload, X, Video, Loader2, Film, ArrowLeft } from 'lucide-react';
import { API } from '../config/api';



const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB for reels
const MAX_CAPTION_LENGTH = 500;

export default function ReelCreatePage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const fileInputRef = useRef(null);

  // Redirect if not authenticated (after auth check completes)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.info('Please sign in to create a Soundstage Clip');
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Check if user has completed their profile
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      // For now, we allow all authenticated users to post reels
      // Add profile checks here if needed
      console.log('[ReelCreate] User authenticated:', user.handle || user.name);
    }
  }, [authLoading, isAuthenticated, user]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('[ReelCreate] File selected:', file.name, file.type, file.size);

    // Validate file type
    const isVideo = file.type.startsWith('video/');
    const validExtensions = ['mp4', 'mov', 'webm'];
    const ext = file.name.split('.').pop().toLowerCase();
    
    if (!isVideo && !validExtensions.includes(ext)) {
      toast.error('Only video files are supported (MP4, MOV, WebM)');
      return;
    }

    // Check file size
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video too large (max 100MB)');
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!videoFile) {
      toast.error('Please select a video');
      return;
    }

    if (caption.length > MAX_CAPTION_LENGTH) {
      toast.error(`Caption too long (max ${MAX_CAPTION_LENGTH} characters)`);
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    
    try {
      console.log('[ReelCreate] Starting upload...');
      
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('caption', caption.trim());

      const headers = getAuthHeaders();
      delete headers['Content-Type']; // Let browser set multipart boundary

      // Simulate progress for better UX (real progress requires XMLHttpRequest)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 300);

      const res = await fetch(`${API}/reels/create`, {
        method: 'POST',
        headers,
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await res.json();
      
      if (!res.ok) {
        console.error('[ReelCreate] Server error:', data);
        throw new Error(data.detail || 'Failed to create clip');
      }

      console.log('[ReelCreate] Clip created:', data);
      toast.success('Soundstage Clip posted!');
      navigate('/reels');
    } catch (err) {
      console.error('[ReelCreate] Error:', err);
      toast.error(err.message || 'Failed to create clip');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const clearVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoFile(null);
    setVideoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="reel-create-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/reels')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-bold">Create Soundstage Clip</h1>
        </div>

        <Card className="glass-card border-border p-6">
          {/* Video Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Video *</label>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="video-input"
            />
            
            {!videoPreview ? (
              <button
                type="button"
                onClick={triggerFileSelect}
                className="w-full aspect-[9/16] max-h-[400px] rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-4 hover:border-secondary/50 hover:bg-muted/30 transition-all cursor-pointer"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                data-testid="upload-button"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-secondary" />
                </div>
                <div className="text-center">
                  <p className="font-medium">Tap to select video</p>
                  <p className="text-sm text-muted-foreground mt-1">MP4, MOV, or WebM • Max 100MB</p>
                </div>
              </button>
            ) : (
              <div className="relative rounded-xl overflow-hidden bg-black">
                <video 
                  src={videoPreview} 
                  controls
                  playsInline
                  className="w-full aspect-[9/16] max-h-[400px] object-contain"
                  data-testid="video-preview"
                />
                <button
                  type="button"
                  onClick={clearVideo}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-background/50 flex items-center justify-center hover:bg-background/70 transition-colors z-10"
                  data-testid="clear-video-button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Caption */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Caption <span className="text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption..."
              maxLength={MAX_CAPTION_LENGTH}
              rows={3}
              className="bg-muted/30 border-border resize-none"
              data-testid="caption-input"
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {caption.length}/{MAX_CAPTION_LENGTH}
            </p>
          </div>

          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div className="mb-4">
              <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-secondary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 text-center">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading || !videoFile}
            className="w-full bg-secondary hover:bg-secondary/80"
            size="lg"
            data-testid="submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Film className="w-4 h-4 mr-2" />
                Post Clip
              </>
            )}
          </Button>

          {/* Tips */}
          <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-border">
            <h3 className="font-medium text-sm mb-2">Tips for great Soundstage Clips:</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Vertical videos (9:16) work best</li>
              <li>• Keep it under 60 seconds for best engagement</li>
              <li>• Use good lighting and stable shots</li>
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
}
